import * as React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  MdArrowBack,
  MdArrowForward,
  MdCheck,
  MdSave,
} from 'react-icons/md';
import { APPRoutes } from '../../Utils/Route';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { toast } from 'react-toastify';
import OrganizationFormModal from '../../Components/Organizations/OrganizationFormModal';
import { getProviderById, getProviderDetailByNPI } from '../../Services/ProviderService';
import PersonalInfo from '../../Components/Providers/PersonalInfo';
import EducationTraining from '../../Components/Providers/EducationTraining';
import PracticeEmployer from '../../Components/Providers/PracticeEmployer';
import FacilityAffiliations from '../../Components/Providers/FacilityAffiliations';
import WorkHistory from '../../Components/Providers/WorkHistory';
import PeerReferences from '../../Components/Providers/PeerReferences';
import Licensure from '../../Components/Providers/Licensure';
import Certifications from '../../Components/Providers/Certifications';
import MedicalMalpractice from '../../Components/Providers/MedicalMalpractice';
import HealthInfo from '../../Components/Providers/HealthInfo';
import EventLog from '../../Components/Providers/EventLog';
import DotLottie from '../../Components/Common/DotLottie';

const TAB_ITEMS = [
  { id: 'personal-info', label: 'Personal Info' },
  { id: 'education-training', label: 'Education & Training' },
  { id: 'practice-employer', label: 'Practice/ Employer' },
  { id: 'facility-affiliations', label: 'Facility Affiliations' },
  { id: 'work-history', label: 'Work History' },
  { id: 'peer-references', label: 'Peer References' },
  { id: 'licensure', label: 'Licensure' },
  { id: 'certifications', label: 'Certifications' },
  { id: 'medical-malpractice', label: 'Medical Malpractice' },
  { id: 'health-info', label: 'Health info' },
  { id: 'event-log', label: 'Event Log' },
];

const PROVIDER_ID_STORAGE_KEY = 'providerForm.providerId';
const ORGANIZATION_ID_STORAGE_KEY = 'providerForm.organizationId';

const renderActiveTab = (tabId, onContinue, onSaveSuccess, providerId, initialData) => {
  switch (tabId) {
    case 'personal-info':
      return <PersonalInfo providerId={providerId} initialData={initialData} onContinue={onContinue} onSaveSuccess={onSaveSuccess} />;
    case 'education-training':
      return <EducationTraining providerId={providerId} initialData={initialData} onContinue={onContinue} onSaveSuccess={onSaveSuccess} />;
    case 'practice-employer':
      return <PracticeEmployer providerId={providerId} initialData={initialData} onContinue={onContinue} onSaveSuccess={onSaveSuccess} />;
    case 'facility-affiliations':
      return <FacilityAffiliations providerId={providerId} initialData={initialData} onContinue={onContinue} onSaveSuccess={onSaveSuccess} />;
    case 'work-history':
      return <WorkHistory providerId={providerId} initialData={initialData} onContinue={onContinue} onSaveSuccess={onSaveSuccess} />;
    case 'peer-references':
      return <PeerReferences providerId={providerId} initialData={initialData} onContinue={onContinue} onSaveSuccess={onSaveSuccess} />;
    case 'licensure':
      return <Licensure providerId={providerId} initialData={initialData} onContinue={onContinue} onSaveSuccess={onSaveSuccess} />;
    case 'certifications':
      return <Certifications providerId={providerId} initialData={initialData} onContinue={onContinue} onSaveSuccess={onSaveSuccess} />;
    case 'medical-malpractice':
      return <MedicalMalpractice providerId={providerId} initialData={initialData} onContinue={onContinue} onSaveSuccess={onSaveSuccess} />;
    case 'health-info':
      return <HealthInfo providerId={providerId} initialData={initialData} onContinue={onContinue} onSaveSuccess={onSaveSuccess} />;
    case 'event-log':
      return <EventLog providerId={providerId} initialData={initialData} onContinue={onContinue} onSaveSuccess={onSaveSuccess} />;
    default:
      return null;
  }
};

function ProvidersFormContent({ location, navigate, token, decodedToken }) {
  const { id: routeProviderId } = useParams();
  const isEditRoute = Boolean(routeProviderId);
  const providerFromState = location.state?.provider || null;
  const [providerFromApi, setProviderFromApi] = React.useState(null);
  const [activeTabIndex, setActiveTabIndex] = React.useState(0);
  const [windowStart, setWindowStart] = React.useState(0);
  const [completedTabs, setCompletedTabs] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const searchParams = new URLSearchParams(location.search);
  const providerIdFromQuery = searchParams.get('providerId');
  const providerIdFromStorage = React.useMemo(() => {
    try {
      return localStorage.getItem(PROVIDER_ID_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to read provider id from localStorage:', error);
      return null;
    }
  }, []);

  const [npiNumber, setNpiNumber] = React.useState('');
  const [isNpiValidated, setIsNpiValidated] = React.useState(Boolean(routeProviderId || providerIdFromQuery || providerIdFromStorage || providerFromState));
  const [validationLoading, setValidationLoading] = React.useState(false);
  const [validationError, setValidationError] = React.useState('');

  const getProviderId = (provider) =>
    provider?.id ?? provider?.Id ?? provider?.ProviderId ?? provider?.provider_id ?? null;

  const getOrganizationId = (provider) =>
    provider?.organization_id ?? provider?.organizationId ?? provider?.OrganizationId ?? provider?.org_id ?? provider?.organization?.id ?? null;

  const initialData = providerFromState || providerFromApi;
  // routeProviderId comes straight from the URL (/providers/edit/:id) and is authoritative —
  // it's the last fallback so a miscased/missing id field on the fetched or state-passed
  // provider object can never leave providerId null while genuinely editing.
  const providerId = getProviderId(providerFromState) ?? getProviderId(providerFromApi) ?? providerIdFromQuery ?? providerIdFromStorage ?? routeProviderId ?? null;
  const isCreateMode = !isEditRoute && !providerId && !providerFromState;

  React.useEffect(() => {
    if (!isNpiValidated && (routeProviderId || providerFromState || providerIdFromQuery || providerIdFromStorage)) {
      setIsNpiValidated(true);
    }
  }, [isNpiValidated, routeProviderId, providerFromState, providerIdFromQuery, providerIdFromStorage]);

  React.useEffect(() => {
    if (!isNpiValidated && providerFromApi) {
      setIsNpiValidated(true);
    }
  }, [isNpiValidated, providerFromApi]);

  React.useEffect(() => {
    const providerIdValue = getProviderId(providerFromState) ?? getProviderId(providerFromApi);
    const organizationIdValue = getOrganizationId(providerFromState) ?? getOrganizationId(providerFromApi);
    if (!providerIdValue) return;

    try {
      localStorage.setItem(PROVIDER_ID_STORAGE_KEY, String(providerIdValue));
      if (organizationIdValue != null) {
        localStorage.setItem(ORGANIZATION_ID_STORAGE_KEY, String(organizationIdValue));
      }
    } catch (error) {
      console.error('Failed to save provider/organization id to localStorage:', error);
    }
  }, [providerFromState, providerFromApi]);

  React.useEffect(() => {
    // On the /providers/edit/:id route, the URL id is authoritative and always re-fetched —
    // location.state.provider (handed over by the list/view page) is only ever used as an
    // instant prefill while this resolves, never trusted in place of a server-verified id.
    // Off that route, fall back to the legacy behavior: trust a full state object if present,
    // otherwise resolve from the query param / localStorage remnants of an earlier session.
    const providerIdToLoad = routeProviderId || (providerFromState ? null : providerIdFromQuery || providerIdFromStorage);
    if (!token || !providerIdToLoad) return;

    let isMounted = true;
    const loadProvider = async () => {
      setLoading(true);
      try {
        const response = await getProviderById(token, providerIdToLoad);
        if (isMounted && response?.is_success) {
          setProviderFromApi(response.data);
        } else if (isMounted && response?.is_success === false) {
          toast.error(response?.message || 'Failed to load provider details.');
        }
      } catch (error) {
        console.error('Error loading provider by ID:', error);
        if (isMounted) {
          toast.error('Failed to load provider details.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProvider();
    return () => {
      isMounted = false;
    };
  }, [token, routeProviderId, providerFromState, providerIdFromQuery, providerIdFromStorage]);

  const activeTab = TAB_ITEMS[activeTabIndex];
  const isLastTab = activeTabIndex === TAB_ITEMS.length - 1;
  const VISIBLE_TAB_COUNT = 5;
  const maxWindowStart = Math.max(0, TAB_ITEMS.length - VISIBLE_TAB_COUNT);

  const updateActiveTab = React.useCallback(
    (nextIndexOrUpdater) => {
      const nextIndex = typeof nextIndexOrUpdater === 'function'
        ? nextIndexOrUpdater(activeTabIndex)
        : nextIndexOrUpdater;

      setActiveTabIndex(nextIndex);
      if (nextIndex < windowStart) {
        setWindowStart(nextIndex);
      } else if (nextIndex >= windowStart + VISIBLE_TAB_COUNT) {
        setWindowStart(Math.min(nextIndex - VISIBLE_TAB_COUNT + 1, maxWindowStart));
      }
    },
    [activeTabIndex, windowStart, maxWindowStart],
  );

  const handlePreviousTab = React.useCallback(() => {
    updateActiveTab((prev) => Math.max(prev - 1, 0));
  }, [updateActiveTab]);

  const handleValidateNpi = React.useCallback(async () => {
    setValidationError('');
    const trimmedNpi = npiNumber.trim();

    if (!trimmedNpi) {
      setValidationError('NPI Number is required to continue.');
      return;
    }

    setValidationLoading(true);
    try {
      const response = await getProviderDetailByNPI(token, trimmedNpi);
      if (response?.is_success) {
        const data = response?.data ?? null;
        if (data) {
          // Check every id casing getProviderId() checks — matching an already-existing
          // provider by NPI must carry its id forward, or the next save creates a dupe.
          const resolvedId = getProviderId(data);
          setProviderFromApi(resolvedId ? { ...data, id: resolvedId, Id: resolvedId, provider_id: resolvedId, ProviderId: resolvedId } : data);
          if (resolvedId) {
            try {
              localStorage.setItem(PROVIDER_ID_STORAGE_KEY, String(resolvedId));
            } catch (error) {
              console.error('Failed to write provider id to localStorage:', error);
            }
          }
        }
        setIsNpiValidated(true);
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        setValidationError(response?.message || 'Failed to fetch provider details by NPI');
      }
    } catch (error) {
      console.error('Error validating provider NPI:', error);
      setValidationError(error?.message || 'Error validating provider NPI');
    } finally {
      setValidationLoading(false);
    }
  }, [npiNumber, token]);

  const handleSaveTab = React.useCallback(() => {
    setCompletedTabs((prev) => {
      if (prev.includes(activeTabIndex)) return prev;
      return [...prev, activeTabIndex];
    });
    toast.success(`${activeTab.label} saved successfully`);
  }, [activeTab.label, activeTabIndex]);

  const handleContinue = React.useCallback(
    (savedValues, savedProviderId) => {
      handleSaveTab();

      if (savedProviderId) {
        // Provider.id is a UUID on the backend — never Number() it, that's NaN
        // for a UUID and would wipe the id right after a successful save.
        const resolvedId = String(savedProviderId);
        setProviderFromApi((prev) => ({
          ...(prev || {}),
          ...(savedValues || {}),
          id: resolvedId,
          Id: resolvedId,
          provider_id: resolvedId,
          ProviderId: resolvedId,
        }));
        try {
          localStorage.setItem(PROVIDER_ID_STORAGE_KEY, resolvedId);
          if (savedValues?.organization_id) {
            localStorage.setItem(ORGANIZATION_ID_STORAGE_KEY, String(savedValues.organization_id));
          }
        } catch (error) {
          console.error('Failed to update provider id in localStorage:', error);
        }
      } else if (savedValues) {
        setProviderFromApi((prev) => ({
          ...(prev || {}),
          ...(savedValues || {}),
        }));
      }

      if (isLastTab) {
        toast.success('Provider form completed');
        navigate(APPRoutes.PROVIDERS);
        return;
      }
      setActiveTabIndex((prev) => Math.min(prev + 1, TAB_ITEMS.length - 1));
    },
    [handleSaveTab, isLastTab, navigate]
  );

  React.useEffect(() => {
    return () => {
      try {
        localStorage.removeItem(PROVIDER_ID_STORAGE_KEY);
        localStorage.removeItem(ORGANIZATION_ID_STORAGE_KEY);
      } catch (error) {
        console.error('Failed to clear provider form localStorage keys:', error);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <DotLottie />
      </div>
    );
  }

  return (
    <>
      {(!isCreateMode || isNpiValidated) && (
        <div className="container-fluid p-0">
          <div className="rounded-4 bg-white p-2 p-md-4 shadow-md border-0">
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div className="d-flex align-items-center gap-3">
                <button
                  type="button"
                  className="btn btn-light border rounded-circle shadow-sm"
                  onClick={() => navigate(APPRoutes.PROVIDERS)}
                  title="Back to Providers"
                  style={{
                    width: 40,
                    height: 40,
                    cursor: 'pointer',
                  }}
                >
                  &larr;
                </button>
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <h3 className="fw-bold mb-0 hc-text-primary">{isCreateMode ? 'Add Provider' : 'Edit Provider'}</h3>

                  </div>
                  <small className="text-muted">{isCreateMode ? 'Complete the provider registration.' : 'Update the provider registration.'}</small>
                </div>
              </div>
            </div>

            <div className="p-3 shadow rounded-4 mb-3">
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-light border rounded-circle"
                  onClick={() => setWindowStart((prev) => Math.max(prev - 1, 0))}
                  disabled={windowStart === 0}
                  aria-label="Scroll tabs left"
                >
                  <MdArrowBack size={18} />
                </button>
                <div className="overflow-hidden w-100">
                  <ul className="nav nav-pills gap-2 d-flex" style={{ minWidth: '100%' }}>
                    {TAB_ITEMS.slice(windowStart, windowStart + VISIBLE_TAB_COUNT).map((tab, index) => {
                      const actualIndex = windowStart + index;
                      const isActive = actualIndex === activeTabIndex;
                      const isCompleted = completedTabs.includes(actualIndex);

                      return (
                        <li key={tab.id} className="nav-item flex-grow-1" style={{ minWidth: 180 }}>
                          <button
                            type="button"
                            className={`nav-link border px-3 py-2 rounded-3 text-capitalize fw-semibold w-100 text-center d-flex align-items-center justify-content-center gap-2 text-wrap ${isActive
                                ? 'hc-bg-primary text-white'
                                : isCompleted
                                  ? 'bg-success text-white'
                                  : 'bg-light text-secondary'
                              }`}
                            onClick={() => setActiveTabIndex(actualIndex)}
                            style={{ cursor: 'pointer' }}
                          >
                            {isCompleted && !isActive && (
                              <span className="badge bg-white text-success fw-bold p-1 d-inline-flex align-items-center"><MdCheck size={12} /></span>
                            )}
                            {tab.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-light border rounded-circle"
                  onClick={() => setWindowStart((prev) => Math.min(prev + 1, maxWindowStart))}
                  disabled={windowStart >= maxWindowStart}
                  aria-label="Scroll tabs right"
                >
                  <MdArrowForward size={18} />
                </button>
              </div>
            </div>

            <div className="rounded-4 border p-3 p-md-4 bg-white shadow-sm">
              <div className="d-flex align-items-center justify-content-between mb-4 flex-column flex-md-row gap-3">
                <div>
                  <h5 className="fw-semibold mb-1">{activeTab.label}</h5>
                  <p className="text-secondary mb-0 small">Fill in the information for this section.</p>
                </div>
                <div className="text-secondary small">
                  Section {activeTabIndex + 1} of {TAB_ITEMS.length}
                </div>
              </div>
              {renderActiveTab(activeTab.id, handleContinue, handleSaveTab, providerId, initialData)}
            </div>
          </div>
        </div>
      )}

      <OrganizationFormModal
        open={isCreateMode && !isNpiValidated}
        title="Validate Provider NPI"
        submitLabel={validationLoading ? 'Validating...' : 'Validate NPI'}
        onClose={() => navigate(APPRoutes.PROVIDERS)}
        onSubmit={(event) => {
          event.preventDefault();
          handleValidateNpi();
        }}
        loading={validationLoading}
      >
        <p className="text-secondary small mb-3 mt-0">
          Enter the 10-digit provider NPI to fetch provider details and continue.
        </p>
        <div className="mb-0">
          <input
            type="text"
            className="form-control rounded-3 shadow-sm"
            placeholder="Enter 10-digit NPI Number"
            value={npiNumber}
            onChange={(event) => setNpiNumber(String(event.target.value).replace(/\D/g, '').slice(0, 10))}
            disabled={validationLoading}
          />
          {validationError ? (
            <div className="form-text text-danger mt-2">{validationError}</div>
          ) : null}
        </div>
      </OrganizationFormModal>
    </>
  );
}

export default function ProvidersForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken]
  );
  const canAccessPage =
    permissionHas(userPermissionCodes, PermissionCodes.PROVIDERS_CREATE, decodedToken) ||
    permissionHas(userPermissionCodes, PermissionCodes.PROVIDERS_EDIT, decodedToken);

  if (!canAccessPage) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger">You do not have permission to access this page.</div>
      </div>
    );
  }

  return (
    <ProvidersFormContent
      location={location}
      navigate={navigate}
      token={token}
      decodedToken={decodedToken}
    />
  );
}
